import { Injectable, inject } from '@angular/core';
import { ApiEndpoints } from '@nano/core/api';
import { AuthService, isUserDemoProfile } from '@nano/core/auth';
import { CONFIG } from '@nano/core/config';
import { BehaviorSubject, NEVER, Observable, Subject, timer } from 'rxjs';
import { delay, dematerialize, finalize, materialize, switchMap, take, throttleTime } from 'rxjs/operators';

const THROTTLE_TIME = 1000;
const DELAY = 700;

@Injectable({ providedIn: 'root' })
export class SseService {
  readonly accountOpen$: Observable<MessageEvent>;
  readonly beneficiaryCreated$: Observable<MessageEvent>;
  readonly cardUpdated$: Observable<MessageEvent>;
  readonly card3DSecureEnabled$: Observable<MessageEvent>;
  readonly idDataUpdated$: Observable<MessageEvent>;
  readonly idDeclined$: Observable<MessageEvent>;
  readonly incomingMessage$: Observable<MessageEvent>;
  readonly paymentEntryUpdated$: Observable<MessageEvent>;
  readonly partyRejected$: Observable<MessageEvent>;
  readonly partyUpdated$: Observable<MessageEvent>;
  readonly partyAvailableBalanceUpdated$: Observable<MessageEvent>;
  readonly paymentBatchFailed$: Observable<MessageEvent>;
  readonly userLanguageChanged$: Observable<MessageEvent>;
  readonly exchangeRateChanged$: Observable<MessageEvent>;
  readonly ibProductUpdated$: Observable<MessageEvent>;
  readonly cardCtpEvent$: Observable<MessageEvent>;

  private accountOpenSubject: Subject<MessageEvent> = new Subject();
  private beneficiaryCreatedSubject: Subject<MessageEvent> = new Subject();
  private cardUpdatedSubject: Subject<MessageEvent> = new Subject();
  private card3DSecureEnabledSubject: Subject<MessageEvent> = new Subject();
  private idDataUpdatedSubject: Subject<MessageEvent> = new Subject();
  private idDeclinedSubject: Subject<MessageEvent> = new Subject();
  private incomingMessageSubject: Subject<MessageEvent> = new Subject();
  private paymentEntryUpdatedSubject: Subject<MessageEvent> = new Subject();
  private partyRejectedSubject: Subject<MessageEvent> = new Subject();
  private partyUpdatedSubject: Subject<MessageEvent> = new Subject();
  private partyAvailableBalanceUpdatedSubject: Subject<MessageEvent> = new Subject();
  private paymentBatchFailedSubject: Subject<MessageEvent> = new Subject();
  private userLanguageChangedSubject: Subject<MessageEvent> = new Subject();
  exchangeRateChangedSubject: Subject<MessageEvent> = new Subject();
  private ibProductUpdatedSubject: Subject<MessageEvent> = new Subject();
  private cardCtpEventSubject: Subject<MessageEvent> = new Subject();

  private eventSource: EventSource;

  private error$ = new BehaviorSubject<boolean>(false);

  private config = inject(CONFIG);

  private reconnectIntervalSeconds = this.config.notificationsReconnectIntervalSeconds;
  private reconnectMaxAttempts = this.config.notificationsReconnectMaxAttempts;
  private reconnectAttempts = 0;
  private selectedPartyId: string;

  constructor(private authService: AuthService) {
    this.accountOpen$ = this.accountOpenSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.beneficiaryCreated$ = this.beneficiaryCreatedSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.cardUpdated$ = this.cardUpdatedSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.card3DSecureEnabled$ = this.card3DSecureEnabledSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.idDataUpdated$ = this.idDataUpdatedSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.idDeclined$ = this.idDeclinedSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.incomingMessage$ = this.incomingMessageSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.paymentEntryUpdated$ = this.paymentEntryUpdatedSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.partyRejected$ = this.partyRejectedSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.partyUpdated$ = this.partyUpdatedSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.partyAvailableBalanceUpdated$ = this.partyAvailableBalanceUpdatedSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.paymentBatchFailed$ = this.paymentBatchFailedSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.userLanguageChanged$ = this.userLanguageChangedSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.exchangeRateChanged$ = this.exchangeRateChangedSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.ibProductUpdated$ = this.ibProductUpdatedSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));
    this.cardCtpEvent$ = this.cardCtpEventSubject.asObservable().pipe(throttleTime(THROTTLE_TIME), delay(DELAY));

    this.authService.userProfile$.subscribe((userProfile) => {
      if (userProfile) {
        this.selectedPartyId = !isUserDemoProfile(userProfile) ? userProfile.partyId : null;
        this.connect();
      } else {
        this.error$.next(false);
        this.close();
      }
    });

    this.error$
      .pipe(
        switchMap((error) => (error ? timer(2000, this.reconnectIntervalSeconds * 1000).pipe(materialize()) : NEVER)),
        dematerialize()
      )
      .subscribe((attempt) => this.attemptingToReconnect(attempt));
  }

  private close(): void {
    this.eventSource?.close();
    this.eventSource = null;
  }

  private connect(): void {
    if (!this.eventSource || this.isEventSourceClosed()) {
      this.eventSource = new EventSource(ApiEndpoints.notifications(this.config.oauthEnabled, this.selectedPartyId).url, { withCredentials: true });
      this.eventSource.onerror = () => {
        this.close();
        if (!this.error$.value) {
          this.error$.next(true);
        }
      };

      this.eventSource.addEventListener('FORCE_LOGOUT', () => this.forceLogout('sseForceLogoutEvent'));
      this.eventSource.addEventListener('ACCOUNT_OPENED', (event: MessageEvent) => this.accountOpenSubject.next(event));
      this.eventSource.addEventListener('CARD_UPDATED', (event: MessageEvent) => this.cardUpdatedSubject.next(event));
      this.eventSource.addEventListener('CARD_3D_SECURE_ENABLED', (event: MessageEvent) => this.card3DSecureEnabledSubject.next(event));
      this.eventSource.addEventListener('IDENTIFICATION_DATA_UPDATED', (event: MessageEvent) => this.idDataUpdatedSubject.next(event));
      this.eventSource.addEventListener('IDENTIFICATION_DECLINED', (event: MessageEvent) => this.idDeclinedSubject.next(event));
      this.eventSource.addEventListener('MESSAGE_SENT', (event: MessageEvent) => this.incomingMessageSubject.next(event));
      this.eventSource.addEventListener('PAYMENT_ENTRY_UPDATED', (event: MessageEvent) => this.paymentEntryUpdatedSubject.next(event));
      this.eventSource.addEventListener('PARTY_REJECTED', (event: MessageEvent) => this.partyRejectedSubject.next(event));
      this.eventSource.addEventListener('PARTY_UPDATED', (event: MessageEvent) => this.partyUpdatedSubject.next(event));
      this.eventSource.addEventListener('PARTY_AVAILABLE_BALANCE_UPDATED', (event: MessageEvent) => this.partyAvailableBalanceUpdatedSubject.next(event));
      this.eventSource.addEventListener('PAYMENT_BATCH_FAILED', (event: MessageEvent) => this.paymentBatchFailedSubject.next(event));
      this.eventSource.addEventListener('BENEFICIARY_CREATED', (event: MessageEvent) => this.beneficiaryCreatedSubject.next(event));
      this.eventSource.addEventListener('LANGUAGE_CHANGED', (event: MessageEvent) => this.userLanguageChangedSubject.next(event));
      this.eventSource.addEventListener('EXCHANGE_RATE_UPDATED', (event: MessageEvent) => this.exchangeRateChangedSubject.next(event));
      this.eventSource.addEventListener('IB_PRODUCT_UPDATED', (event: MessageEvent) => this.ibProductUpdatedSubject.next(event));
      this.eventSource.addEventListener('CARD_ENROLLED_TO_CTP', (event: MessageEvent) => {
        this.cardCtpEventSubject.next(event);
      });
      this.eventSource.addEventListener('CARD_DELETED_FROM_CTP', (event: MessageEvent) => {
        this.cardCtpEventSubject.next(event);
      });
    }
  }

  private forceLogout(reasonMessage: string): void {
    this.authService
      .logout(undefined, reasonMessage)
      .pipe(
        take(1),
        finalize(() => this.close())
      )
      .subscribe();
  }

  private attemptingToReconnect(attempt: number): void {
    this.reconnectAttempts = attempt + 1;
    if (!this.isEventSourceClosed()) {
      console.debug(`[notifications] ready state is ${this.eventSource.readyState === EventSource.OPEN.valueOf() ? 'open' : 'connecting'}.`);
      this.error$.next(false);
      this.reconnectAttempts = 0;
    } else if (this.reconnectAttempts > this.reconnectMaxAttempts) {
      console.debug(`[notifications] maximum number of reconnection attempts has been reached, forced logout.`);
      this.error$.next(false);
      this.reconnectAttempts = 0;
      this.forceLogout('sseReconnectExhausted');
    } else {
      console.debug(`[notifications] attempting to reconnect #${this.reconnectAttempts}...`);
      this.connect();
    }
  }

  private isEventSourceClosed = (): boolean => ![EventSource.OPEN.valueOf(), EventSource.CONNECTING.valueOf()].includes(this.eventSource?.readyState);
}
